import { useEffect, useState } from "react";
import axios from "axios";
import {
  Filter, Users, Video, Eye, UserCheck, Wallet, TrendingDown, Loader2,
} from "lucide-react";
import { getAuthHeaders } from "../lib/adminAuth";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const fmt = (n) => (n || 0).toLocaleString("de-CH");

const STAGE_META = {
  leads: { icon: Users, color: "#8B5CF6" },
  webinar_reg: { icon: Video, color: "#6366F1" },
  webinar_att: { icon: Eye, color: "#3B82F6" },
  customers: { icon: UserCheck, color: "#0EA5E9" },
  deposited: { icon: Wallet, color: "#10B981" },
};

const RANGES = [
  { days: 30, label: "30 Tage" },
  { days: 90, label: "90 Tage" },
  { days: 0, label: "Gesamt" },
];

// Kurzer, umsetzbarer Tipp pro Stufe — wird bei der Stufe mit dem größten
// Drop-off angezeigt, damit der Partner weiß, wo er ansetzen kann.
const STAGE_TIPS = {
  webinar_reg: "Tipp: Lade deine Leads persönlich per WhatsApp zum nächsten Live-Webinar ein — dein Reflink findest du unter Links.",
  webinar_att: "Tipp: Erinnere Angemeldete am Webinar-Tag nochmal kurz — die meisten No-Shows vergessen den Termin einfach.",
  customers: "Tipp: Melde dich innerhalb von 48h nach dem Webinar bei Teilnehmern — dann ist das Interesse am höchsten.",
  deposited: "Tipp: Begleite neue Kunden aktiv durchs Onboarding (Broker-Registrierung + Einzahlung) — hier springen die meisten ab.",
};

export const PartnerFunnel = () => {
  const [days, setDays] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/partner/funnel?days=${days}`, { headers: getAuthHeaders() })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const stages = data?.stages || [];
  const maxCount = Math.max(1, ...stages.map((s) => s.count));
  // Stufe mit dem größten Drop-off → dort den Tipp anzeigen.
  const worstStage = stages.reduce(
    (worst, s) => (s.drop_off > (worst?.drop_off || 0) ? s : worst),
    null,
  );

  return (
    <section className="al-card overflow-hidden" data-testid="partner-funnel">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-violet-50/40 to-transparent">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              <Filter className="h-3.5 w-3.5 text-[var(--al-purple)]" /> Dein Funnel
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Wo verlierst du Kunden? Von deinen Leads bis zur Einzahlung — nur deine eigenen Kontakte.
            </p>
          </div>
          <div className="inline-flex p-1 rounded-full bg-white border border-[var(--al-line)] shadow-sm">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`partner-funnel-range-${r.days}`}
                className={`px-3 py-1 rounded-full text-[11px] font-display font-semibold transition-all ${
                  days === r.days ? "al-grad-bg text-white shadow-sm" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400 gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Lade Funnel…
          </div>
        ) : !data ? (
          <div className="text-sm text-slate-500 py-4">Funnel konnte nicht geladen werden.</div>
        ) : (
          <>
            <div className="space-y-3">
              {stages.map((s) => {
                const meta = STAGE_META[s.id] || STAGE_META.leads;
                const Icon = meta.icon;
                const width = Math.max(5, (s.count / maxCount) * 100);
                return (
                  <div key={s.id} data-testid={`partner-funnel-stage-${s.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: meta.color }} />
                        <span className="font-display font-semibold text-sm">{s.label}</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {s.conversion_pct != null && (
                          <span
                            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                              s.conversion_pct >= 50
                                ? "bg-emerald-100 text-emerald-700"
                                : s.conversion_pct >= 20
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-rose-100 text-rose-700"
                            }`}
                          >
                            {s.conversion_pct} %
                          </span>
                        )}
                        <span className="font-mono-tab font-bold text-sm">{fmt(s.count)}</span>
                      </div>
                    </div>
                    <div className="h-7 rounded-lg bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-lg transition-all duration-500"
                        style={{
                          width: `${width}%`,
                          background: `linear-gradient(90deg, ${meta.color}, ${meta.color}99)`,
                        }}
                      />
                    </div>
                    {s.drop_off > 0 && (
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-rose-500">
                        <TrendingDown className="h-3 w-3" /> −{fmt(s.drop_off)} verloren
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {worstStage && STAGE_TIPS[worstStage.id] && (
              <div
                className="mt-5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 leading-relaxed"
                data-testid="partner-funnel-tip"
              >
                <strong>Größter Drop-off: {worstStage.label}.</strong> {STAGE_TIPS[worstStage.id]}
              </div>
            )}

            <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
              Gezählt werden nur Leads &amp; Kunden, die dir zugeordnet sind (Reflink / Empfehlungsgeber).
              Stufen sind unabhängige Mengen — Kunden können auch ohne Webinar einsteigen.
            </p>
          </>
        )}
      </div>
    </section>
  );
};
